import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import LoadingScreen from "../LoadingScreen";
import { get } from "../../apis";
import { toast } from "react-toastify";
import {
  Modal,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const AddressInputModal = ({ open, onSave, onCancel, initialAddress }) => {
  const [address, setAddress] = useState("");
  const [plusAddress, setPlusAddress] = useState("");

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedWard, setSelectedWard] = useState("");

  const [selectedCityName, setSelectedCityName] = useState("");
  const [selectedDistrictName, setSelectedDistrictName] = useState("");
  const [selectedWardName, setSelectedWardName] = useState("");

  const { t } = useTranslation();

  const { data, error, isLoading } = useSWR(
    "https://raw.githubusercontent.com/ductridev/DiaGioiHanhChinhVN/master/data.json",
    get,
    {
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        // Only retry up to 10 times.
        if (retryCount >= 10) return;

        // Retry after 10 seconds.
        setTimeout(() => revalidate({ retryCount }), 10000);
      },
    }
  );

  useEffect(() => {
    if (initialAddress) {
      if (initialAddress.address) setAddress(initialAddress.address);
      if (initialAddress.plusAddress)
        setPlusAddress(initialAddress.plusAddress);
    }
  }, []);
  useEffect(() => {
    if (!isLoading && !error) {
      setCities(data);
    }
  }, [data]);
  useEffect(() => {
    if (error && !isLoading) toast(t("cannot_fetch_address_data"));
  }, [error]);

  useEffect(() => {
    if (cities && initialAddress && initialAddress.selectedCity) {
      setSelectedCity(initialAddress.selectedCity);
      setSelectedCityName(initialAddress.selectedCityName);
    }
  }, [cities]);

  useEffect(() => {
    if (selectedCity && cities) {
      const city = cities.find((city) => city.Id == selectedCity);
      setSelectedCityName(city.Name)
      if (city) {
        setDistricts(city.Districts);
      }
    }
  }, [cities, selectedCity]);

  useEffect(() => {
    if (districts && initialAddress && initialAddress.selectedDistrict) {
      setSelectedDistrict(initialAddress.selectedDistrict);
      setSelectedDistrictName(initialAddress.selectedDistrictName);
    }
  }, [districts]);

  useEffect(() => {
    if (selectedDistrict) {
      const district = districts.find(
        (district) => district.Id == selectedDistrict
      );
      setSelectedDistrictName(district.Name)
      if (district) {
        setWards(district.Wards);
      }
    }
  }, [districts, selectedDistrict]);

  useEffect(() => {
    if (wards && initialAddress && initialAddress.selectedWard) {
      setSelectedWard(initialAddress.selectedWard);
      setSelectedWardName(initialAddress.selectedWardName);
    }
  }, [wards]);

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    setSelectedCity(cityId);
    setSelectedDistrict("");
    setSelectedWard("");
  };

  const handleDistrictChange = (event) => {
    const districtId = event.target.value;
    setSelectedDistrict(districtId);
    setSelectedWard("");
  };

  const handleWardChange = (event) => {
    setSelectedWard(event.target.value);
    const ward = wards.find(
      (ward) => ward.Id == event.target.value
    );
    setSelectedWardName(ward.Name);
  };

  const handleSave = () => {
    onSave({
      address: `${selectedCityName}, ${selectedDistrictName}, ${selectedWardName}`,
      plusAddress,
      selectedCity,
      selectedDistrict,
      selectedWard,
      selectedCityName,
      selectedDistrictName,
      selectedWardName,
    });
  };

  return isLoading ? (
    <LoadingScreen />
  ) : error ? null : (
    <Modal open={open.isAddress} onClose={onCancel}>
      <Box sx={modalStyle}>
        <Typography variant="h6" component="h2" sx={{ marginBottom: "20px" }}>
          {t("address")}
        </Typography>
        <div className="mb-3 row">
          <div className="col-sm-12 d-flex">
            <FormControl className="col-sm-4 me-2">
              <InputLabel id="city-label" sx={{ background: "white" }}>
                {t("city")}
              </InputLabel>
              <Select
                labelId="city-label"
                id="city"
                value={selectedCity}
                onChange={handleCityChange}
                fullWidth
              >
                <MenuItem value="">
                  <em>{t("city")}</em>
                </MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city.Id} value={city.Id}>
                    {city.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className="col-sm-4 me-2" disabled={!selectedCity}>
              <InputLabel id="district-label" sx={{ background: "white" }}>
                {t("district")}
              </InputLabel>
              <Select
                labelId="district-label"
                id="district"
                value={selectedDistrict}
                onChange={handleDistrictChange}
                fullWidth
              >
                <MenuItem value="">
                  <em>{t("district")}</em>
                </MenuItem>
                {districts.map((district) => (
                  <MenuItem key={district.Id} value={district.Id}>
                    {district.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className="col-sm-4" disabled={!selectedDistrict}>
              <InputLabel id="ward-label" sx={{ background: "white" }}>
                {t("ward")}
              </InputLabel>
              <Select
                labelId="ward-label"
                id="ward"
                value={selectedWard}
                onChange={handleWardChange}
                fullWidth
              >
                <MenuItem value="">
                  <em>{t("ward")}</em>
                </MenuItem>
                {wards.map((ward) => (
                  <MenuItem key={ward.Id} value={ward.Id}>
                    {ward.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        <div className="row align-items-center mb-3">
          <label htmlFor="plusAddress" className="col-sm-2 col-form-label">
            {t("detailed_address")}
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              id="plusAddress"
              name="plusAddress"
              placeholder={t("detailed_address")}
              className="form-control"
              value={plusAddress}
              onChange={(e) => setPlusAddress(e.target.value)}
            />
          </div>
        </div>
        <div className="d-flex justify-content-between">
          <Button variant="contained" color="secondary" onClick={onCancel}>
            {t("cancel")}
          </Button>
          <Button variant="contained" color="success" onClick={handleSave}>
            {t("save")}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default AddressInputModal;
